import { ChangeEvent } from 'react';

// Shadcn Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Icons
import { ArrowLeft } from "lucide-react";

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { usePasswordReset } from '../../hooks/authentication/usePasswordReset';

export const ForgotPassword = () => {
  const { navigateWithTenant } = useTenantNavigation();
  const {
    formData,
    emailSent,
    passwordError,
    resetSuccess,
    handleInputChange,
    handleRequestSubmit,
    handleResetSubmit
  } = usePasswordReset();

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  const queryParams = new URLSearchParams(window.location.search);
  const showResetRequest = !emailSent && !queryParams.has("token");

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12" 
      style={gradientStyle}
    >
      <div className="w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="px-8 py-6">
          <div className="flex flex-col items-center">
            <img 
              src="/branding/TriEvents_Loading_Big_Text.gif" 
              alt="TriEvents Logo" 
              className="w-40 sm:w-48 h-auto"
            />
            {showResetRequest && (
              <>
                <h1 className="text-2xl font-semibold text-foreground">Reset Password</h1>
                <p className="text-muted-foreground text-center text-sm mt-0.5 mb-4">
                  We will send an email to reset your password
                </p>
              </>
            )}
            {queryParams.has("token") && !resetSuccess && (
              <h1 className="text-2xl font-semibold text-foreground">Reset Password</h1>
            )}
            {resetSuccess && (
              <>
                <h1 className="text-2xl font-semibold text-foreground">Success!</h1>
                <p className="text-muted-foreground text-center text-sm mt-0.5 mb-4">
                  Your password has been successfully reset!
                </p>
              </>
            )}
          </div>

          {showResetRequest && (
            <form onSubmit={handleRequestSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit"
                size="lg"
                className="w-full transition-transform hover:scale-105"
              >
                Send Email
              </Button>
            </form>
          )}

          {emailSent && (
            <Alert className="mt-4">
              <AlertDescription>
                If this email is part of this organization, an email has been sent with a link to reset your password!
              </AlertDescription>
            </Alert>
          )}

          {queryParams.has("token") && !resetSuccess && (
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">New Password</Label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Enter your new password"
                  value={formData.password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('password', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('confirmPassword', e.target.value)}
                  required
                />
              </div>
              
              {/* Password Requirements */}
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium">Password Requirements:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>At least 8 characters long</li>
                  <li>At least one lowercase letter (a-z)</li>
                  <li>At least one uppercase letter (A-Z)</li>
                  <li>At least one number (0-9)</li>
                  <li>At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                </ul>
              </div>
              
              {passwordError && (
                <Alert variant="destructive">
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}
              <Button 
                type="submit"
                size="lg"
                className="w-full transition-transform hover:scale-105"
              >
                Reset Password
              </Button>
            </form>
          )}

          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-6 pt-4">
            <Button
              variant="outline"
              onClick={() => navigateWithTenant('/login')}
              className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

